import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Tabs,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Button,
  Table,
  Tag,
  Descriptions,
  Space,
  Modal,
  Typography,
  Popconfirm,
  Row,
  Col,
  message,
} from "antd";
import moment from "moment";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEmployee } from "Hooks/AdminHooks/Organization/useEmployee";
import PayrollService from "Services/EmployeeServices/PayrollService";
import BonusService from "Services/EmployeeServices/BonusService";
import { BonusTypeOptions, BonusTypeEnumLabel } from "Constants/Enums/BonusTypeEnum";
import { BonusStatusEnumLabel, BonusStatusColor } from "Constants/Enums/BonusStatusEnum";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const money = (v, currency) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  const amt = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${amt}` : amt;
};
const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const bonusTypeLabel = (v) =>
  BonusTypeEnumLabel[v] || (typeof v === "string" ? v : String(v ?? "-"));
const bonusStatusTag = (v) => {
  const label = BonusStatusEnumLabel[v] || (typeof v === "string" ? v : String(v ?? "-"));
  return <Tag color={BonusStatusColor[v] || "default"}>{label}</Tag>;
};

const PayrollAdmin = () => {
  const { employees, loading: employeesLoading } = useEmployee();
  const employeeOptions = useMemo(
    () =>
      (Array.isArray(employees) ? employees : []).map((e) => ({
        value: e.id,
        label: `${e.fullName || e.email || "Employee"}${e.publicId ? ` (${e.publicId})` : ""}`,
      })),
    [employees]
  );
  const nameById = useMemo(() => {
    const map = {};
    (Array.isArray(employees) ? employees : []).forEach((e) => {
      map[e.id] = e.fullName || e.email || `#${e.id}`;
    });
    return map;
  }, [employees]);

  // ----- Run Payroll -----
  const [payForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [paycheck, setPaycheck] = useState(null);

  const onRunPayroll = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        employeeId: values.employeeId,
        periodStart: values.period[0].format("YYYY-MM-DD"),
        periodEnd: values.period[1].format("YYYY-MM-DD"),
        regularGrossPay: Number(values.regularGrossPay),
        federalTax: Number(values.federalTax || 0),
        stateTax: Number(values.stateTax || 0),
        otherDeductions: Number(values.otherDeductions || 0),
      };
      const result = await PayrollService.createPaycheck(payload);
      setPaycheck(result || null);
      message.success("Paycheck created");
    } catch (e) {
      message.error(
        e.response?.data?.details ||
          e.response?.data?.title ||
          "Failed to create paycheck. Check the period, compensation and amounts."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Bonuses -----
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const loadPending = async () => {
    try {
      setPendingLoading(true);
      const data = await BonusService.getPending();
      setPending(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error("Failed to load pending bonuses");
      setPending([]);
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  // ----- All bonuses (HR-wide, Mudassir Jun-21) -----
  const [allBonuses, setAllBonuses] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allStatus, setAllStatus] = useState("");

  const loadAllBonuses = async (status) => {
    try {
      setAllLoading(true);
      const data = await BonusService.getAll(status || undefined);
      setAllBonuses(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error("Failed to load bonuses");
      setAllBonuses([]);
    } finally {
      setAllLoading(false);
    }
  };

  useEffect(() => {
    loadAllBonuses("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approveBonus = async (bonusId) => {
    try {
      setReviewing(true);
      await BonusService.review(bonusId, { isApproved: true, remarks: "Approved" });
      message.success("Bonus approved");
      loadPending();
    } catch (e) {
      message.error(e.response?.data?.details || "Failed to approve bonus");
    } finally {
      setReviewing(false);
    }
  };

  // Reject modal
  const [rejectId, setRejectId] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const submitReject = async () => {
    if (!rejectRemarks.trim()) {
      message.warning("Please add a remark for the rejection");
      return;
    }
    try {
      setReviewing(true);
      await BonusService.review(rejectId, { isApproved: false, remarks: rejectRemarks.trim() });
      message.success("Bonus rejected");
      setRejectId(null);
      setRejectRemarks("");
      loadPending();
    } catch (e) {
      message.error(e.response?.data?.details || "Failed to reject bonus");
    } finally {
      setReviewing(false);
    }
  };

  // Create-bonus modal
  const [createForm] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const onCreateBonus = async (values) => {
    try {
      setCreating(true);
      const payload = {
        employeeId: values.employeeId,
        bonusType: values.bonusType,
        amount: Number(values.amount),
        currency: values.currency?.trim() || "USD",
        awardDate: values.awardDate.format("YYYY-MM-DD"),
        reason: values.reason?.trim() || "",
      };
      await BonusService.create(payload);
      message.success("Bonus created (approved, waiting for payroll)");
      setCreateOpen(false);
      createForm.resetFields();
    } catch (e) {
      message.error(
        e.response?.data?.details || e.response?.data?.title || "Failed to create bonus"
      );
    } finally {
      setCreating(false);
    }
  };

  const pendingColumns = [
    { title: "Employee", key: "emp", render: (_, r) => nameById[r.employeeId] || `#${r.employeeId}` },
    { title: "Type", key: "type", render: (_, r) => bonusTypeLabel(r.bonusType) },
    { title: "Amount", key: "amount", render: (_, r) => money(r.amount, r.currency) },
    { title: "Award Date", key: "award", render: (_, r) => fmtDate(r.awardDate) },
    { title: "Status", key: "status", render: (_, r) => bonusStatusTag(r.status) },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v) => v || "-" },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Space>
          <Popconfirm
            title="Approve this bonus?"
            okText="Approve"
            okButtonProps={{ loading: reviewing }}
            onConfirm={() => approveBonus(r.id)}
          >
            <Button type="link">Approve</Button>
          </Popconfirm>
          <Button type="link" danger onClick={() => setRejectId(r.id)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  const runPayrollTab = (
    <Row gutter={24}>
      <Col xs={24} md={10}>
        <Form form={payForm} layout="vertical" onFinish={onRunPayroll}>
          <Form.Item
            name="employeeId"
            label="Employee"
            rules={[{ required: true, message: "Select an employee" }]}
          >
            <Select
              showSearch
              loading={employeesLoading}
              placeholder="Select employee"
              options={employeeOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="period"
            label="Pay Period"
            rules={[{ required: true, message: "Select the pay period" }]}
          >
            <RangePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="regularGrossPay"
            label="Regular Gross Pay"
            extra="Do not add bonuses here. Approved bonuses in the period are added automatically."
            rules={[{ required: true, message: "Enter regular gross pay" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 2500" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="federalTax" label="Federal Tax" initialValue={0}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="stateTax" label="State Tax" initialValue={0}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="otherDeductions" label="Other" initialValue={0}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Button type="primary" htmlType="submit" loading={submitting} className="btn-primary">
            Create Paycheck
          </Button>
        </Form>
      </Col>

      <Col xs={24} md={14}>
        {paycheck ? (
          <Card size="small" title={`Paycheck ${paycheck.publicId || `#${paycheck.id}`}`}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Regular Gross">
                {money(paycheck.regularGrossPay, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Bonus Pay">
                {money(paycheck.bonusPay, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Gross Pay">
                {money(paycheck.grossPay, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Federal Tax">
                {money(paycheck.federalTax, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="State Tax">
                {money(paycheck.stateTax, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Other Deductions">
                {money(paycheck.otherDeductions, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Unpaid Leave Deduction">
                {money(paycheck.unpaidLeaveDeduction, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Deductions">
                {money(paycheck.totalDeductions, paycheck.currency)}
              </Descriptions.Item>
              <Descriptions.Item label="Net Pay">
                <Text strong>{money(paycheck.netPay, paycheck.currency)}</Text>
              </Descriptions.Item>
            </Descriptions>
            {Array.isArray(paycheck.bonuses) && paycheck.bonuses.length > 0 && (
              <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                {paycheck.bonuses.length} bonus(es) included.
              </Text>
            )}
            {Array.isArray(paycheck.leaveDeductions) && paycheck.leaveDeductions.length > 0 && (
              <Text type="secondary" style={{ display: "block" }}>
                {paycheck.leaveDeductions.length} unpaid-leave deduction(s) applied.
              </Text>
            )}
          </Card>
        ) : (
          <Card size="small">
            <Text type="secondary">
              Fill the form and create a paycheck. The calculated gross, deductions and net pay will
              show here.
            </Text>
          </Card>
        )}
      </Col>
    </Row>
  );

  const bonusesTab = (
    <>
      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <Button type="primary" className="btn-primary" onClick={() => setCreateOpen(true)}>
          Create Bonus
        </Button>
      </div>
      <Table
        className="custom-table"
        rowKey={(r) => r.id}
        loading={pendingLoading}
        columns={pendingColumns}
        dataSource={pending}
        pagination={{ pageSize: 8 }}
        locale={{ emptyText: "No bonus recommendations waiting for review" }}
      />
    </>
  );

  const allBonusColumns = [
    {
      title: "Employee",
      key: "emp",
      render: (_, r) => r.employeeName || nameById[r.employeeId] || `#${r.employeeId}`,
    },
    { title: "Department", key: "dept", render: (_, r) => r.departmentName || "-" },
    { title: "Type", key: "type", render: (_, r) => bonusTypeLabel(r.bonusType) },
    { title: "Amount", key: "amount", render: (_, r) => money(r.amount, r.currency) },
    { title: "Award Date", key: "award", render: (_, r) => fmtDate(r.awardDate) },
    { title: "Status", key: "status", render: (_, r) => bonusStatusTag(r.status) },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v) => v || "-" },
  ];

  const allBonusesTab = (
    <>
      <div style={{ marginBottom: 16 }}>
        <Select
          value={allStatus}
          style={{ width: 200 }}
          onChange={(v) => {
            setAllStatus(v);
            loadAllBonuses(v);
          }}
          options={[
            { value: "", label: "All Statuses" },
            { value: "Recommended", label: "Recommended" },
            { value: "Approved", label: "Approved" },
            { value: "Rejected", label: "Rejected" },
            { value: "Paid", label: "Paid" },
          ]}
        />
      </div>
      <Table
        className="custom-table"
        rowKey={(r) => r.id}
        loading={allLoading}
        columns={allBonusColumns}
        dataSource={allBonuses}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: "No bonuses found" }}
      />
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card title="Payroll & Bonuses" className="card-professional" style={{ margin: 16 }}>
        <Tabs
          items={[
            { key: "run", label: "Run Payroll", children: runPayrollTab },
            { key: "bonus", label: "Bonus Approvals", children: bonusesTab },
            { key: "all", label: "All Bonuses", children: allBonusesTab },
          ]}
        />
      </Card>

      {/* Reject bonus modal */}
      <Modal
        title="Reject bonus"
        open={rejectId !== null}
        onCancel={() => {
          setRejectId(null);
          setRejectRemarks("");
        }}
        onOk={submitReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: reviewing }}
      >
        <Text>Add a remark explaining the rejection:</Text>
        <TextArea
          rows={3}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          maxLength={500}
          style={{ marginTop: 8 }}
        />
      </Modal>

      {/* Create bonus modal */}
      <Modal
        title="Create Bonus"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        okText="Create"
        okButtonProps={{ loading: creating }}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreateBonus}>
          <Form.Item
            name="employeeId"
            label="Employee"
            rules={[{ required: true, message: "Select an employee" }]}
          >
            <Select
              showSearch
              loading={employeesLoading}
              placeholder="Select employee"
              options={employeeOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="bonusType"
            label="Bonus Type"
            rules={[{ required: true, message: "Select a bonus type" }]}
          >
            <Select placeholder="Select type" options={BonusTypeOptions} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Enter the amount" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="Currency" initialValue="USD">
                <Input placeholder="USD" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="awardDate"
            label="Award Date"
            rules={[{ required: true, message: "Select the award date" }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </DashboardLayout>
  );
};

export default PayrollAdmin;
